// B-57 debug module — realtime subscription lifecycle tracking
// Remove this file once root cause is confirmed.

const DEV = import.meta.env.DEV;

// ── Global subscription registry ─────────────────────────────────────────────

export interface SubRecord {
  id: string;
  hook: string;
  channelTopic: string;
  userId?: string;
  accessTokenTail: string; // last 8 chars — enough to detect token rotation
  createdAt: string;
  status: string;
}

const registry = new Map<string, SubRecord>();

export function regAdd(rec: Omit<SubRecord, 'createdAt'>): void {
  if (!DEV) return;
  const existing = [...registry.values()].find((r) => r.channelTopic === rec.channelTopic);
  if (existing) {
    console.warn(
      `[RT:DUPE] ${rec.hook} — channel "${rec.channelTopic}" already registered by ${existing.hook} ` +
      `(created ${existing.createdAt}). Possible subscription leak.`,
      { existing, incoming: rec }
    );
  }
  registry.set(rec.id, { ...rec, createdAt: new Date().toISOString() });
  _dump();
}

export function regUpdate(id: string, status: string): void {
  if (!DEV) return;
  const rec = registry.get(id);
  if (rec) { rec.status = status; _dump(); }
}

export function regRemove(id: string, reason: string): void {
  if (!DEV) return;
  const rec = registry.get(id);
  if (rec) {
    console.log(`[RT:REMOVE] ${rec.hook} channel="${rec.channelTopic}" reason="${reason}"`);
    registry.delete(id);
  }
  _dump();
}

function _dump(): void {
  const rows = [...registry.values()];
  console.groupCollapsed(`[RT:REGISTRY] ${rows.length} active subscription(s)`);
  rows.forEach((r) => console.log(`  ${r.hook} | ${r.channelTopic} | status=${r.status} | token=…${r.accessTokenTail} | since=${r.createdAt}`));
  console.groupEnd();
}

// Public accessor so dev tools can inspect registry at runtime
(window as any).__RT_REGISTRY__ = registry;

// ── Token helpers ─────────────────────────────────────────────────────────────

export function tokenTail(token: string | null | undefined): string {
  return token ? token.slice(-8) : 'null';
}

export function tokenExp(token: string | null | undefined): string {
  if (!token) return 'no-token';
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    if (typeof exp !== 'number') return 'no-exp';
    const secsLeft = exp - Math.floor(Date.now() / 1000);
    return secsLeft > 0 ? `expires_in=${secsLeft}s` : `EXPIRED_${Math.abs(secsLeft)}s_ago`;
  } catch {
    return 'parse-err';
  }
}

// ── Fake-expired JWT for dev simulation ──────────────────────────────────────

export function makeExpiredJwt(originalToken: string): string {
  try {
    const [header, payloadB64] = originalToken.split('.');
    const payload = JSON.parse(atob(payloadB64));
    payload.exp = Math.floor(Date.now() / 1000) - 120; // expired 2 min ago
    const newPayload = btoa(JSON.stringify(payload)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
    return `${header}.${newPayload}.INVALIDSIG_SIMULATED`;
  } catch {
    // Fallback: return a structurally valid but obviously-expired JWT
    const h = btoa('{"alg":"HS256","typ":"JWT"}').replace(/=/g, '');
    const p = btoa(JSON.stringify({ sub: 'sim', exp: 1, iat: 1 })).replace(/=/g, '');
    return `${h}.${p}.INVALIDSIG_SIMULATED`;
  }
}

// ── Client cache logging ──────────────────────────────────────────────────────

export function logClientCreate(seq: number, tokenTailStr: string): void {
  if (!DEV) return;
  console.log(`[RT:CLIENT] Created GoTrueClient #${seq} for token=…${tokenTailStr}`);
}

export function logClientEvict(tokenTailStr: string): void {
  if (!DEV) return;
  console.log(`[RT:CLIENT] Evicting client token=…${tokenTailStr} — calling removeAllChannels()`);
}

export function logClientUpdate(tokenTailStr: string): void {
  if (!DEV) return;
  console.log(`[RT:CLIENT] updateSupabaseAuth → setAuth(…${tokenTailStr}) on all cached clients`);
}

export function logClientClear(count: number): void {
  if (!DEV) return;
  console.log(`[RT:CLIENT] clearSupabaseClientCache — removing ${count} client(s) + all channels`);
}
